import { useMemo, useRef } from 'react';
import { Perspective } from '@feature/event-detail/types';
import { useGetEventPerspectivesQuery, useGetPostMetadataQuery, PostResponseSchema } from '@/api/client';

interface UseEventPerspectivesParams {
  eventId: string;
}

interface UseEventPerspectivesResult {
  perspectives: Perspective[];
  isLoading: boolean;
  error: any;
}

export function useEventPerspectives({
  eventId,
}: UseEventPerspectivesParams): UseEventPerspectivesResult {
  const { data, isLoading, error } = useGetEventPerspectivesQuery({
    eventId: Number(eventId),
    page: 1,
    size: 50,
  });

  const postIds = useMemo(() => {
    if (!data?.items) return '';
    return data.items.map(post => post.id).join(',');
  }, [data?.items]);

  const { data: metadata } = useGetPostMetadataQuery(postIds, {
    skip: !postIds,
    pollingInterval: 30000,
  });

  const perspectivesRef = useRef<Perspective[]>([]);

  const perspectives: Perspective[] = useMemo(() => {
    if (!data?.items) return [];

    const newPerspectives = data.items.map((post: PostResponseSchema): Perspective => ({
      id: post.id.toString(),
      username: post.username,
      viewerCount: metadata?.[post.id]?.current_viewer_count ?? 0,
      thumbnailUrl: post.photo_url_compressed || undefined,
      postId: post.id.toString(),
      livestreamId: post.livestream_id || '',
      chatChannelId: post.chat_channel_id || '',
      streamStatus: metadata?.[post.id]?.stream_status || 'live',
      m3u8Url: post.m3u8_url || undefined,
    }));

    const previous = perspectivesRef.current;
    if (previous.length === newPerspectives.length) {
      let hasNonViewerCountChange = false;

      for (let i = 0; i < newPerspectives.length; i++) {
        const prev = previous[i];
        const curr = newPerspectives[i];

        if (
          prev.id !== curr.id ||
          prev.username !== curr.username ||
          prev.thumbnailUrl !== curr.thumbnailUrl ||
          prev.livestreamId !== curr.livestreamId ||
          prev.streamStatus !== curr.streamStatus ||
          prev.m3u8Url !== curr.m3u8Url
        ) {
          hasNonViewerCountChange = true;
          break;
        }
      }

      if (!hasNonViewerCountChange) {
        const hasViewerCountChange = previous.some(
          (prev, i) => prev.viewerCount !== newPerspectives[i].viewerCount,
        );

        if (hasViewerCountChange) {
          previous.forEach((prev, i) => {
            prev.viewerCount = newPerspectives[i].viewerCount;
          });
        }

        return previous;
      }
    }

    perspectivesRef.current = newPerspectives;
    return newPerspectives;
  }, [data?.items, metadata]);

  return {
    perspectives,
    isLoading,
    error,
  };
}
